import { useCallback, useEffect } from 'react';
import { takeUntil } from 'rxjs';
import { trpc } from '@client-trpc';
import { filterWidgetEvent } from '@client/foundation/shared/utils/filter-widget-event';
import { useNotifications } from '@client/foundation/notifications/notifications.hook';
import { useWidget } from '@client/widgets/widget.hook';
import type { WidgetPayload } from '@client/widgets/widget.types';
import { useWindow } from '@client/window/window.hook';
import { useDraftState } from '@client/state/draft-form-state.hook';
import { useTradingWorkspaceDestroy$ } from '@client/features/trading-workspace/trading-workspace-scope.context';
import { useObservable } from '@client/foundation/shared/utils/observable.hook';
import { buildLayoutSnapshot } from './layout-snapshot.utils';
import { useCreateLayout } from './create-layout.hook';
import { resolveLayoutStateToFormState } from './layout-template-state.utils';

export const useManageTradingLayouts = () => {
  const { openWidget$, openWidget } = useWidget();
  const { openWindow, closeWindow } = useWindow();
  const { notify } = useNotifications();
  const { state$ } = useDraftState();
  const workspaceDestroy$ = useTradingWorkspaceDestroy$();
  const trpcClient = trpc.useUtils().client;
  const utils = trpc.useUtils();
  const { mutateAsync: deleteLayouts } =
    trpc.tradingLayouts.deleteLayouts.useMutation();
  const { mutateAsync: setLayoutAsDefault } =
    trpc.tradingLayouts.setLayoutAsDefault.useMutation();
  const { mutateAsync: clearLayoutDefault } =
    trpc.tradingLayouts.clearLayoutDefault.useMutation();
  const { mutateAsync: createSharedResource } =
    trpc.sharedResources.createSharedResource.useMutation();
  const { mutateAsync: importSharedResource } =
    trpc.sharedResources.importSharedResource.useMutation();
  const { mutateAsync: updateSharedResource } =
    trpc.sharedResources.updateSharedResource.useMutation();
  const { mutateAsync: revokeSharedResource } =
    trpc.sharedResources.revokeSharedResource.useMutation();
  const { onUpdateSubmit } = useCreateLayout();
  const latestDraftState = useObservable(() => state$, null);

  const handleDelete = useCallback(
    async (layoutIds: string[]) => {
      try {
        const count = await deleteLayouts({ layoutIds });
        await utils.tradingLayouts.layouts.invalidate();

        notify({
          type: 'success',
          message:
            count > 0
              ? `${count} layout${count > 1 ? 's' : ''} deleted successfully`
              : 'No layouts were deleted'
        });
      } catch (error) {
        notify({
          type: 'error',
          message:
            error instanceof Error ? error.message : 'Failed to delete layouts'
        });
      }
    },
    [deleteLayouts, notify, utils]
  );

  const handleSetDefault = useCallback(
    async (layoutId: string, isDefault: boolean) => {
      try {
        if (isDefault) {
          await clearLayoutDefault({ layoutId });
        } else {
          await setLayoutAsDefault({ layoutId });
        }
        await utils.tradingLayouts.layouts.invalidate();

        notify({
          type: 'success',
          message: isDefault
            ? 'Layout unmarked as default'
            : 'Default layout updated'
        });
      } catch (error) {
        notify({
          type: 'error',
          message:
            error instanceof Error
              ? error.message
              : 'Failed to set default layout'
        });
      }
    },
    [clearLayoutDefault, notify, setLayoutAsDefault, utils]
  );

  const handleImportShared = useCallback(
    async (sharedResourceId: string, sharedResourceName: string) => {
      try {
        await importSharedResource({
          sharedResourceId,
          name: sharedResourceName
        });

        await Promise.all([
          utils.tradingLayouts.layouts.invalidate(),
          utils.sharedResources.listSharedResources.invalidate()
        ]);

        notify({
          type: 'success',
          message: 'Shared layout imported'
        });
      } catch (error) {
        notify({
          type: 'error',
          message:
            error instanceof Error
              ? error.message
              : 'Failed to import shared layout'
        });
      }
    },
    [importSharedResource, notify, utils]
  );

  const handleShare = useCallback(
    async (layoutId: string) => {
      try {
        await createSharedResource({
          resourceType: 'tradingLayout',
          layoutId
        });

        await utils.sharedResources.listSharedResources.invalidate();

        notify({
          type: 'success',
          message: 'Layout published to Shared'
        });
      } catch (error) {
        notify({
          type: 'error',
          message:
            error instanceof Error ? error.message : 'Failed to share layout'
        });
      }
    },
    [createSharedResource, notify, utils]
  );

  const handleModifyShared = useCallback(
    async (
      sharedResourceId: string,
      sharedResourceName: string,
      sharedResourceNotes?: string
    ) => {
      try {
        const sharedResource =
          await trpcClient.sharedResources.getSharedResource.query({
            sharedResourceId
          });

        const fallbackSnapshot =
          sharedResource.payload.resourceType === 'tradingLayout'
            ? sharedResource.payload.snapshot
            : null;

        const snapshot = latestDraftState
          ? buildLayoutSnapshot(latestDraftState)
          : fallbackSnapshot;

        if (!snapshot) {
          notify({
            type: 'error',
            message: 'No layout state found'
          });
          return;
        }

        openWindow({
          type: 'SaveTradingLayoutForm',
          sizePreset: 'form',
          componentProps: {
            onSubmit: async ({ name, notes }) => {
              try {
                await updateSharedResource({
                  resourceType: 'tradingLayout',
                  sharedResourceId,
                  name,
                  notes: notes?.trim() || undefined,
                  snapshot
                });

                await utils.sharedResources.listSharedResources.invalidate();

                notify({
                  type: 'success',
                  message: 'Shared layout updated successfully'
                });

                closeWindow('SaveTradingLayoutForm');
              } catch (error) {
                notify({
                  type: 'error',
                  message:
                    error instanceof Error
                      ? error.message
                      : 'Failed to update shared layout'
                });
              }
            },
            initialValues: {
              name: sharedResourceName,
              notes:
                sharedResource.payload.resourceType === 'tradingLayout'
                  ? sharedResource.payload.notes ?? sharedResourceNotes ?? ''
                  : sharedResourceNotes
            },
            submitLabel: 'Update Shared'
          }
        });
      } catch (error) {
        notify({
          type: 'error',
          message:
            error instanceof Error
              ? error.message
              : 'Failed to prepare shared layout update'
        });
      }
    },
    [
      closeWindow,
      latestDraftState,
      notify,
      openWindow,
      trpcClient,
      updateSharedResource,
      utils.sharedResources.listSharedResources
    ]
  );

  const handleRevokeShared = useCallback(
    async (sharedResourceId: string) => {
      try {
        await revokeSharedResource({ sharedResourceId });
        await utils.sharedResources.listSharedResources.invalidate();

        notify({
          type: 'success',
          message: 'Shared layout revoked'
        });
      } catch (error) {
        notify({
          type: 'error',
          message:
            error instanceof Error
              ? error.message
              : 'Failed to revoke shared layout'
        });
      }
    },
    [notify, revokeSharedResource, utils]
  );

  useEffect(() => {
    const subscription = openWidget$()
      .pipe(
        filterWidgetEvent<WidgetPayload, 'ManageTradingLayouts'>(
          'ManageTradingLayouts'
        ),
        takeUntil(workspaceDestroy$)
      )
      .subscribe({
        next: ({ chartId, mode, activeLayoutId }) => {
          const handleApply = async (layoutId: string) => {
            try {
              const layouts = await trpcClient.tradingLayouts.layouts.query();
              const layout = layouts.find(({ id }) => id === layoutId);

              if (!layout) {
                throw new Error('Layout not found');
              }

              const resolvedFormState = await resolveLayoutStateToFormState(
                trpcClient,
                layout.snapshot.formState
              );

              openWidget({
                type: 'TradingForm',
                id: chartId,
                mode,
                formState: resolvedFormState,
                linkedTradingLayoutId: layout.id,
                linkedTradingLayoutName: layout.name,
                initSource: 'LAYOUT_RESTORE',
                defaultTemplatePolicy: 'SKIP_AUTO_APPLY_DEFAULT_TEMPLATES'
              });

              notify({
                type: 'success',
                message: 'Layout applied'
              });

              closeWindow('ManageTradingLayouts');
            } catch (error) {
              notify({
                type: 'error',
                message:
                  error instanceof Error
                    ? error.message
                    : 'Failed to apply layout'
              });
            }
          };

          const handleModify = (
            layoutId: string,
            layoutName: string,
            layoutNotes?: string
          ) => {
            if (!latestDraftState) {
              notify({
                type: 'error',
                message: 'No draft state found'
              });
              return;
            }

            const snapshot = buildLayoutSnapshot(
              latestDraftState
            );

            openWindow({
              type: 'SaveTradingLayoutForm',
              sizePreset: 'form',
              componentProps: {
                onSubmit: onUpdateSubmit(layoutId, snapshot),
                initialValues: { name: layoutName, notes: layoutNotes },
                submitLabel: 'Update'
              }
            });
          };

          openWindow({
            type: 'ManageTradingLayouts',
            sizePreset: 'grid',
            componentProps: {
              activeLayoutId,
              onApply: handleApply,
              onDelete: handleDelete,
              onModify: handleModify,
              onImportShared: handleImportShared,
              onModifyShared: handleModifyShared,
              onRevokeShared: handleRevokeShared,
              onSetDefault: handleSetDefault,
              onShare: (layoutId) => handleShare(layoutId)
            }
          });
        },
        error: (err) =>
          console.error('Error in ManageTradingLayouts subscription:', err)
      });

    return () => subscription.unsubscribe();
  }, [
    closeWindow,
    createSharedResource,
    handleDelete,
    handleShare,
    handleImportShared,
    handleModifyShared,
    handleRevokeShared,
    handleSetDefault,
    latestDraftState,
    notify,
    onUpdateSubmit,
    openWidget,
    openWidget$,
    openWindow,
    trpcClient,
    workspaceDestroy$
  ]);
};
